import { createSharedComposable } from '@vueuse/core'
import { resolveScreen } from '~/ai/screens'

/**
 * A structured, declarative description of what the user is currently looking
 * at. This is the industry-standard "page context" approach (vs. brittle DOM
 * scraping): pages explicitly contribute the entities on screen, and we send a
 * small whitelisted payload to the assistant — never raw DOM or PII.
 */
export interface AiVisibleEntity {
  type: string
  id?: string | number
  label?: string
  [key: string]: unknown
}

/** A clickable target on the current screen, harvested live from the DOM. */
export interface AiClickableTarget {
  target: string
  label: string
}

export interface AiPageContext {
  route: string
  screenId: string
  screenTitle: string
  params: Record<string, string>
  visibleEntities: AiVisibleEntity[]
  /** data-tour anchors currently on screen — the precise "where to click" set. */
  clickableTargets: AiClickableTarget[]
}

/**
 * Read the data-tour anchors currently in the DOM and label them from their
 * accessible name (aria-label > text). Purely code/DOM-derived — no docs.
 */
function harvestClickableTargets(): AiClickableTarget[] {
  if (!import.meta.client) return []
  const els = Array.from(document.querySelectorAll<HTMLElement>('[data-tour]'))
  const seen = new Set<string>()
  const out: AiClickableTarget[] = []
  for (const el of els) {
    const target = el.getAttribute('data-tour') || ''
    if (!target || target.includes('${') || seen.has(target)) continue
    seen.add(target)
    const label = (el.getAttribute('aria-label') || el.textContent || '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 60)
    out.push({ target, label })
  }
  return out.slice(0, 30)
}

const _useAiContext = () => {
  const route = useRoute()
  const entities = ref<AiVisibleEntity[]>([])

  // Re-harvest clickable targets when the route changes (cheap; the assistant
  // reads this lazily when a message is sent).
  const targetsBump = ref(0)
  watch(() => route.fullPath, () => { targetsBump.value++ })

  const context = computed<AiPageContext>(() => {
    const screen = resolveScreen(route.path)
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    targetsBump.value // reactive dependency so re-harvest tracks navigation
    return {
      route: route.path,
      screenId: screen.id,
      screenTitle: screen.title,
      params: (route.params as Record<string, string>) ?? {},
      visibleEntities: entities.value,
      clickableTargets: harvestClickableTargets(),
    }
  })

  function setVisibleEntities(next: AiVisibleEntity[]) {
    entities.value = next
  }

  // Clear page-contributed entities whenever the route changes; pages re-declare
  // on mount.
  watch(
    () => route.fullPath,
    () => {
      entities.value = []
    },
  )

  return { context, setVisibleEntities }
}

export const useAiContext = createSharedComposable(_useAiContext)

/**
 * Per-page helper: declare the entities the user is looking at so the assistant
 * can resolve "this instance" / "this invoice". Reactive and self-clearing.
 *
 * @example
 *   defineAiContext(() => [{ type: 'instance', id: vm.value?.id, label: vm.value?.name, status: vm.value?.status }])
 */
export function defineAiContext(getter: () => AiVisibleEntity[]) {
  const { setVisibleEntities } = useAiContext()
  const route = useRoute()
  const apply = () => setVisibleEntities(getter().filter(Boolean))
  watchEffect(apply)
  // `useAiContext` clears the entities on every route change, which includes
  // switching tabs inside a page that stays mounted (`/instances/42` →
  // `/instances/42/backups`). Nothing the getter reads changes on a tab switch,
  // so the effect above would not re-run and the assistant lost "this
  // instance" on every tab but the first. Registered after that clear, this
  // runs after it.
  watch(() => route.fullPath, apply)
  onUnmounted(() => setVisibleEntities([]))
}
